import React, { Component, Fragment } from 'react'
import { load } from 'recaptcha-v3'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Collapse,
  Form,
  IconManager,
  Level,
  paymentMethodsIconPack,
  Toasts,
  Text,
  MediaQuery,
  Drawer,
  Heading,
  Notification,
} from '@deseretbook/react-ui'
import { withPopupProps } from '../withPopupProps'
import PaymentMethodForm from './PaymentMethodForm'
import { withCheckout } from '../CheckoutContext'
import { DeseretBook } from '../../../dbcom/api'
import { Logger, formatErrorMessage } from '../../../dbcom'
import WalletPaymentSourcesFormField from './WalletPaymentSourcesFormField'
import PaymentSourceDetails from './PaymentSourceDetails'
import PaymentStepAddons from './PaymentStepAddons'
import { FullError } from '../../../errors'

IconManager.register(paymentMethodsIconPack)

const logger = new Logger({ context: 'Checkout' })

/**
 * Calculates how much money is still due on the given order. Returns undefined
 * if not given an actual order instance.
 *
 * @param {object} order - Order to calculate amount due for.
 * @returns {number|undefined} amount due
 */
const calculateAmountDue = (order) => {
  if (!order || !order.payments || !order.total) {
    return 0
  }

  return order.payments
    .filter(({ state }) => state === 'checkout')
    .reduce((total, { amount }) => total - Number(amount), Number(order.total))
}

/**
 * Returns an array of valid payments attached to the order
 *
 * @param {object} order - Order to get valid payments
 * @returns {array} all valid payments
 */
const getValidPayments = (order) =>
  (order.payments || []).filter(
    ({ sourceType, state: checkoutState }) =>
      sourceType === 'Spree::CreditCard' && checkoutState === 'checkout',
  )

/**
 * The Payment step of single page checkout.
 */
class PaymentStep extends Component {
  /**
   * Payment step constructor.
   *
   * @param {object} props - component props
   */
  constructor(props) {
    super(props)

    this.state = {
      /* Currently selected wallet payment source */
      selectedWalletPaymentSource: {},
      /* If the current step is being saved. */
      isSavingStep: false,
      /* Form data for the new card creation. */
      newCardForm: {},
      /* If the create form is currently being displayed. */
      createPaymentFormIsVisible: false,
      /* If the create for, is rendered. Used to lazy-load form content. */
      createPaymentFormIsRendered: false,
      /* Prop value of if the step is currently active. Used in getDerivedStateFromProps. */
      propsIsActive: null,
      /* If we should show the reCaptcha checkbox */
      showCheckboxRecaptcha: false,
    }

    this.stepIsValid = this.stepIsValid.bind(this)
    this.showNewCardForm = this.showNewCardForm.bind(this)
    this.hideNewCardForm = this.hideNewCardForm.bind(this)
    this.onNewCardFormChange = this.onNewCardFormChange.bind(this)
    this.onWalletFormChange = this.onWalletFormChange.bind(this)
    this.createNewPayment = this.createNewPayment.bind(this)
    this.saveStep = this.saveStep.bind(this)
  }

  /**
   * Update payment step if certain props change.
   *
   * @param {object} props - new component props
   * @param {object} state - current component state
   * @returns {object} State based on props
   */
  static getDerivedStateFromProps(props, state) {
    const { order, user, isActive } = props
    const { propsSelectedWalletPaymentSource, propsIsActive } = state

    // TODO I think this can be simplified, I think the API might return the selected
    // wallet payment source for us.

    // Find the selected wallet payment source so we can check to see if it has changed.
    const validCardPayments = getValidPayments(order)

    // Use the payment source from valid payments, or the first CC within the user wallet payment sources
    const walletPaymentSource =
      ((user && user.walletPaymentSources) || []).filter(
        ({ paymentSource }) =>
          (paymentSource &&
            paymentSource.id &&
            validCardPayments[0] &&
            validCardPayments[0].sourceId === paymentSource.id) ||
          (paymentSource && paymentSource.id),
      )[0] || {}

    // Show the new payment form if the user has no cards
    const showNewPaymentForm =
      !validCardPayments.length && user && !(user.walletPaymentSources || []).length

    // If the step has toggled from being displayed or not, OR if the selected payment source
    // has changed, then we want to reset things back to the default state.
    if (
      isActive !== propsIsActive ||
      (walletPaymentSource &&
        walletPaymentSource.id &&
        walletPaymentSource.id !== propsSelectedWalletPaymentSource.id)
    ) {
      return {
        propsIsActive: isActive,
        propsSelectedWalletPaymentSource: walletPaymentSource,
        selectedWalletPaymentSource: walletPaymentSource,
        createPaymentFormIsVisible: showNewPaymentForm,
        newCardForm: {},
      }
    }

    return null
  }

  /**
   * Determines if the create form should be shown initially.
   */
  componentDidMount() {
    const { order, user } = this.props

    const validCardPayments = getValidPayments(order)

    // Show the new card form if there are no cards in validCardPayments and
    // if the user does not have any walletPaymentSources
    if (!validCardPayments.length && user && !(user.walletPaymentSources || []).length) {
      this.showNewCardForm()
    }
  }

  /**
   * onChange handler for the new card form. Applies given changes to the new
   * card form.
   *
   * @param {object} [changes={}] - Changes to be made to the new card.
   */
  onNewCardFormChange(changes = {}) {
    this.setState(({ newCardForm }) => ({
      newCardForm: {
        ...newCardForm,
        ...changes,
      },
    }))
  }

  /**
   * onChange handler for the wallet payment method form. Changes the selected
   * wallet payment source to the specified wallet payment source.
   *
   * @param {event} event - input change event
   */
  onWalletFormChange({ target }) {
    const { value } = target
    const { user } = this.props

    const selectedWalletPaymentSource =
      (user && user.walletPaymentSources.filter(({ id }) => String(id) === String(value))[0]) || {}

    this.setState({ selectedWalletPaymentSource })
  }

  /**
   * Determines if the step is currently valid and can be saved or not.
   * The step is determined to be valid if there is no amount due or if a wallet
   * payment source has been selected and is ready to be applied to the order.
   *
   * @returns {boolean} true if all known validation requirements are met.
   */
  stepIsValid() {
    const { order } = this.props
    const { selectedWalletPaymentSource } = this.state
    const amountDue = calculateAmountDue(order)

    return !amountDue || (selectedWalletPaymentSource && selectedWalletPaymentSource.id)
  }

  /**
   * Displays the new card form.
   * @param {boolean} isMediumOrLarger - Used for opening Drawer on mobile views
   */
  showNewCardForm(isMediumOrLarger) {
    const { popup } = this.props

    if (!isMediumOrLarger && isMediumOrLarger !== undefined) {
      popup.open()
    }
    this.setState({
      // show the form
      createPaymentFormIsVisible: true,
      // make sure the form gets initialized.
      createPaymentFormIsRendered: true,
    })
  }

  /**
   * Hides the new card form, also resets the form values.
   * @param {boolean} isMediumOrLarger - Used for closing Drawer on mobile views
   */
  hideNewCardForm(isMediumOrLarger) {
    const { popup } = this.props

    if (!isMediumOrLarger && isMediumOrLarger !== undefined) {
      popup.close()
    }
    this.setState({
      // hide the form
      createPaymentFormIsVisible: false,
      // reset the form
      newCardForm: {},
    })
  }

  /**
   * Creates a new payment based on the given hosted fields object.
   *
   * TODO This is basically a duplicate of the `saveStep` method. If you can
   * think of a clean way to combine the two that would be great. My issue was
   * trying to figure out how to mix the hostedFields tokenizing without having
   * to set the step loading variables multiple times.
   *
   * @param {object} params.hostedFields - hostedFields data used to create payment with.
   * @param {boolean} params.useDeliveryAddress - Whether we use the deliver address as the billing address or not.
   * @param {object} params.recaptchaToken - Token returned from recaptcha checkbox
   */
  createNewPayment({ hostedFields, useDeliveryAddress, recaptchaToken }) {
    const {
      order,
      updateOrder,
      updateUser,
      startLoading,
      stopLoading,
      availablePaymentMethods,
      user,
    } = this.props
    const { newCardForm, showCheckboxRecaptcha } = this.state

    const paymentMethod = availablePaymentMethods.filter(
      ({ type }) => type === 'Solidus::Gateway::BraintreeGateway',
    )[0]

    if (!paymentMethod) {
      Toasts.danger('Unable to save', 'Unable to process credit cards')

      return
    }

    const address = useDeliveryAddress && order.shipAddress ? order.shipAddress : newCardForm

    let newCardRecaptchaToken = recaptchaToken

    startLoading(() => {
      this.setState({ isSavingStep: true }, () => {
        hostedFields.tokenize(async (tokenizeError, payload) => {
          try {
            if (tokenizeError) {
              throw new Error(tokenizeError)
            }

            // Use V3 recaptcha if checkbox is false and the token isn't present
            if (!showCheckboxRecaptcha && !newCardRecaptchaToken) {
              /* eslint-disable-next-line no-underscore-dangle */
              const siteKeyV3 = window.__GLOBALS__.config.recaptchaSiteKeyV3

              if (siteKeyV3) {
                const reCaptcha = await load(siteKeyV3)

                newCardRecaptchaToken = await reCaptcha.execute('credit_cards/create')
              }
            }

            const addressValues = {
              ...address,
              state: undefined,
              country: undefined,
            }

            // If there's a user we want to break out of default Solidus behavior. We want
            // the credit card to immediately be saved to the user, rather than after the
            // order is completed.
            if (user && user.id) {
              // Create the new card
              const newCard = await DeseretBook.user(user)
                .creditCard()
                .create({
                  cardholderName: `${addressValues.firstname} ${addressValues.lastname}`,
                  paymentMethodNonce: payload.nonce,
                  billingAddress: addressValues,
                  recaptchaToken: newCardRecaptchaToken,
                })

              if (newCard.error) {
                throw new FullError(newCard)
              }

              // Fetch the user's updated wallet.
              const { walletPaymentSources } = await DeseretBook.user(user)
                .walletPaymentSource()
                .all({ perPage: 100 })

              // Now, figure out which is the newly selected wallet source.
              const selectedWalletPaymentSource = walletPaymentSources.find(
                (source) => source.paymentSource && source.paymentSource.id === newCard.id,
              )

              // Update the user with the recently fetched wallet.
              updateUser({ walletPaymentSources })

              // Select the new wallet source and then save the order with the new card.
              this.setState({ selectedWalletPaymentSource }, this.saveStep)
            } else {
              const updatedOrder = await DeseretBook.checkout(order).update({
                order: {
                  state: 'payment',
                  paymentsAttributes: [
                    {
                      paymentMethodId: paymentMethod.id,
                      paymentMethodNonce: payload.nonce,
                      sourceAttributes: {
                        name: `${addressValues.firstname} ${addressValues.lastname}`,
                        addressAttributes: addressValues,
                      },
                    },
                  ],
                },
                recaptchaToken: newCardRecaptchaToken,
              })

              if (updatedOrder.error) {
                throw new FullError(updatedOrder)
              }

              updateOrder(updatedOrder, true)
              stopLoading()
              this.setState({ isSavingStep: false })
              Toasts.success('Payment saved')
            }
          } catch (error) {
            const errors = (error && error.errors) || ''
            const message = (error && error.message) || ''

            if (errors.includes('Failed Recaptcha')) {
              this.setState({ showCheckboxRecaptcha: true })
            }

            stopLoading()
            this.setState({ isSavingStep: false })
            Toasts.danger('Unable to save', formatErrorMessage(message, errors))
            logger.error(`Unable to create new payment "${formatErrorMessage(message, errors)}"`, {
              errors,
              order,
            })
          }
        })
      })
    })
  }

  /**
   * Attempts to save the payment step and progress the order to the review step.
   */
  saveStep() {
    const { order, updateOrder, startLoading, stopLoading } = this.props

    startLoading(() => {
      this.setState({ isSavingStep: true }, async () => {
        const { selectedWalletPaymentSource } = this.state

        try {
          let updatedOrder = {}

          // If there's no selected wallet payment source then we can just try
          // to advance the step. Otherwise we apply the selected payment source.
          if (!selectedWalletPaymentSource || !selectedWalletPaymentSource.id) {
            updatedOrder = await DeseretBook.checkout(order).advance()
          } else {
            updatedOrder = await DeseretBook.checkout(order).update({
              order: {
                state: 'payment',
                paymentsAttributes: [
                  {
                    paymentMethodId: selectedWalletPaymentSource.paymentSource.paymentMethodId,
                    sourceAttributes: {
                      walletPaymentSourceId: selectedWalletPaymentSource.id,
                    },
                  },
                ],
                billAddressAttributes: ((selectedWalletPaymentSource || {}).paymentSource || {})
                  .address,
              },
            })
          }

          if (updatedOrder.error) {
            throw new Error(updatedOrder.error)
          }

          updateOrder(updatedOrder, true)
          Toasts.success('Payment saved')
          this.setState({ isSavingStep: false })
          stopLoading()
        } catch ({ message, errors }) {
          Toasts.danger('Unable to save', message)
          logger.error(`Unable to save payment step: "${message}"`, { errors })
          stopLoading()
          this.setState({ isSavingStep: false })
        }
      })
    })
  }

  /**
   * Determines how the payment step should be rendered.
   *
   * @returns {function} Component
   */
  render() {
    const { user, order, onCloseStep, status, availablePaymentMethods, popup } = this.props
    const {
      createPaymentFormIsVisible,
      createPaymentFormIsRendered,
      isSavingStep,
      selectedWalletPaymentSource,
      newCardForm,
      showCheckboxRecaptcha,
    } = this.state

    const stepIsValid = this.stepIsValid()
    const sources = (user && user.walletPaymentSources) || []

    // Not all cards are saved. We need to filter out the valid payments that
    // aren't part of the user's wallet so we can still display that information.
    const nonWalletCardPayments =
      (order.payments || []).filter(
        ({ sourceType, state, sourceId }) =>
          sourceType === 'Spree::CreditCard' &&
          state === 'checkout' &&
          !sources.filter(({ paymentSourceId }) => paymentSourceId === sourceId).length,
      ) || []

    const creditCardPaymentMethod = availablePaymentMethods.filter(
      ({ type }) => type === 'Solidus::Gateway::BraintreeGateway',
    )[0]

    const canCreateCreditCards = status.services.braintree

    return (
      <MediaQuery.Medium>
        {(isMediumOrLarger) => (
          <Fragment>
            {/* Default Payment View */}
            <Collapse isCollapsed={createPaymentFormIsVisible && isMediumOrLarger}>
              {/* Show non-wallet payments that are currently applied. */}
              {!!nonWalletCardPayments.length && (
                <Box paddingBottom={1}>
                  <Heading>Selected Payment</Heading>
                  {nonWalletCardPayments.map(({ source, id }) => (
                    <PaymentSourceDetails source={source} key={id} />
                  ))}
                </Box>
              )}

              {/* Show the user's wallet form if the user has cards in their wallet */}
              {!!sources.length && (
                <Form isSubmitting={isSavingStep} onSubmit={this.saveStep}>
                  <Heading>My Saved Cards</Heading>
                  <WalletPaymentSourcesFormField
                    name="id"
                    sources={sources}
                    value={selectedWalletPaymentSource ? selectedWalletPaymentSource.id : undefined}
                    onChange={this.onWalletFormChange}
                  />
                </Form>
              )}
              {canCreateCreditCards ? (
                <Level>
                  <Level.Left>
                    <Level.Item>
                      <Button
                        {...popup.triggerProps}
                        type="button"
                        disabled={status.isLoading}
                        onClick={() => this.showNewCardForm(isMediumOrLarger)}
                      >
                        Add New Card
                      </Button>
                    </Level.Item>
                  </Level.Left>
                </Level>
              ) : (
                <Notification type="warning" titleContent="Unable to create new cards">
                  We&#39;re sorry for the inconvenience. It looks like our payment processor is
                  having difficulties. We are unable to process new credit cards at this time.{' '}
                  {!!sources.length && 'You can still use your saved cards however.'}
                </Notification>
              )}

              <PaymentStepAddons availablePaymentMethods={availablePaymentMethods} />

              {/* Step Actions */}
              <Level>
                <Level.Right>
                  {order.state !== 'payment' && (
                    <Level.Item>
                      <Button type="button" onClick={onCloseStep} disabled={isSavingStep}>
                        Cancel
                      </Button>
                    </Level.Item>
                  )}

                  <Level.Item>
                    <Button
                      type="button"
                      color="action"
                      onClick={this.saveStep}
                      isLoading={isSavingStep}
                      disabled={status.isLoading || !stepIsValid}
                    >
                      {order.state === 'payment' ? 'Continue' : 'Save Changes'}
                    </Button>
                  </Level.Item>
                </Level.Right>
              </Level>
            </Collapse>

            {/* Mobile version of the create card form. */}
            <Drawer
              {...popup.popupProps}
              close={() => popup.isOpen && this.hideNewCardForm(isMediumOrLarger)}
            >
              <Drawer.Header>
                <Text isBold hasEllipsis>
                  Create New Card
                </Text>
              </Drawer.Header>
              <Drawer.Content>
                {createPaymentFormIsRendered && (
                  <PaymentMethodForm
                    address={newCardForm}
                    onChange={this.onNewCardFormChange}
                    onSubmit={this.createNewPayment}
                    onCancel={this.hideNewCardForm}
                    isSubmitting={isSavingStep}
                    paymentMethod={creditCardPaymentMethod}
                    showCheckboxRecaptcha={showCheckboxRecaptcha}
                  />
                )}
              </Drawer.Content>
            </Drawer>

            {/* Desktop version of the create card form. */}
            <Collapse isCollapsed={!(createPaymentFormIsVisible && isMediumOrLarger)}>
              {createPaymentFormIsRendered ? (
                <PaymentMethodForm
                  address={newCardForm}
                  onChange={this.onNewCardFormChange}
                  onSubmit={this.createNewPayment}
                  onCancel={this.hideNewCardForm}
                  isSubmitting={isSavingStep}
                  paymentMethod={creditCardPaymentMethod}
                  showCheckboxRecaptcha={showCheckboxRecaptcha}
                />
              ) : (
                <div />
              )}
            </Collapse>
          </Fragment>
        )}
      </MediaQuery.Medium>
    )
  }
}

PaymentStep.propTypes = {
  order: PropTypes.shape({
    payments: PropTypes.array,
    total: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    walletPaymentSources: PropTypes.array,
  }),
  status: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.bool, PropTypes.objectOf(PropTypes.bool)]),
  ),
  availablePaymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    }),
  ),
  updateUser: PropTypes.func,
  updateOrder: PropTypes.func,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  onCloseStep: PropTypes.func,
  popup: PropTypes.shape({
    triggerProps: PropTypes.object,
    popupProps: PropTypes.object,
    close: PropTypes.func,
    open: PropTypes.func,
    isOpen: PropTypes.bool,
  }),
}

PaymentStep.defaultProps = {
  user: {
    walletPaymentSources: [],
  },
  status: {},
  availablePaymentMethods: [],
  updateUser: () => {},
  updateOrder: () => {},
  startLoading: () => {},
  stopLoading: () => {},
  onCloseStep: () => {},
}

export default withCheckout(withPopupProps(PaymentStep))
